/**
 * DETERMINISTIC MATH for the simulation.
 *
 * ECMAScript only requires +, −, ×, ÷ and Math.sqrt to be correctly rounded.
 * Math.sin/cos/atan2/hypot/exp/pow/log are "implementation-approximated", and
 * V8 (Chrome/Edge), SpiderMonkey (Firefox) and JavaScriptCore (Safari) really
 * do return different last bits for some inputs. Solo play never notices, but
 * online co-op runs the same sim on every peer in lockstep, so one differing
 * bit in a bullet angle forks the run (and forces a full-state resync).
 *
 * These functions are ports of the FreeBSD/Sun fdlibm algorithms using only
 * correctly-rounded operations, so every engine produces identical bits.
 * Accuracy is within ~1 ulp of the platform functions for the ranges the game
 * uses. Everything under src/sim, src/content and the bot must use these
 * instead of Math.* for transcendental functions (tests/dmath.test.ts
 * enforces that).
 */

/* ------------------------------------------------------------ bit helpers */

const f64 = new Float64Array(1);
const u32 = new Uint32Array(f64.buffer);
// Little-endian on every platform browsers ship on; detect anyway.
const HI = new Uint8Array(new Uint16Array([1]).buffer)[0] === 1 ? 1 : 0;
const LO = 1 - HI;

/** x · 2^n, exact (no rounding for normal results). */
function scalbn(x: number, n: number): number {
  // Split large exponents so each factor is a representable power of two.
  while (n > 1023) {
    x *= 8.98846567431158e307; // 2^1023
    n -= 1023;
  }
  while (n < -1022) {
    x *= 2.2250738585072014e-308; // 2^-1022
    n += 1022;
  }
  u32[HI] = ((n + 1023) << 20) >>> 0;
  u32[LO] = 0;
  return x * f64[0];
}

/* ---------------------------------------------------------------- sin/cos */

const S1 = -1.66666666666666324348e-1;
const S2 = 8.33333333332248946124e-3;
const S3 = -1.98412698298579493134e-4;
const S4 = 2.75573137070700676789e-6;
const S5 = -2.50507602534068634195e-8;
const S6 = 1.58969099521155010221e-10;

const C1 = 4.16666666666666019037e-2;
const C2 = -1.38888888888741095749e-3;
const C3 = 2.48015872894767294178e-5;
const C4 = -2.75573143513906633035e-7;
const C5 = 2.08757232129817482790e-9;
const C6 = -1.13596475577881948265e-11;

function kSin(x: number): number {
  const z = x * x;
  const v = z * x;
  const r = S2 + z * (S3 + z * (S4 + z * (S5 + z * S6)));
  return x + v * (S1 + z * r);
}

function kCos(x: number): number {
  const z = x * x;
  const r = z * (C1 + z * (C2 + z * (C3 + z * (C4 + z * (C5 + z * C6)))));
  const hz = 0.5 * z;
  const w = 1 - hz;
  return w + ((1 - w - hz) + z * r);
}

const INV_PIO2 = 6.36619772367581382433e-1;
const PIO2_1 = 1.57079632673412561417;
const PIO2_1T = 6.07710050650619224932e-11;
const PIO4 = 0.7853981633974483;

/** Reduce x to r ∈ [−π/4, π/4] and quadrant n (0–3). Cody–Waite, exact ops only. */
let reducedR = 0;
function reduce(x: number): number {
  const k = Math.round(x * INV_PIO2);
  reducedR = x - k * PIO2_1 - k * PIO2_1T;
  return ((k % 4) + 4) % 4;
}

export function dsin(x: number): number {
  if (x !== x || x === Infinity || x === -Infinity) return NaN;
  if (x > -PIO4 && x < PIO4) return kSin(x);
  const n = reduce(x);
  const r = reducedR;
  return n === 0 ? kSin(r) : n === 1 ? kCos(r) : n === 2 ? -kSin(r) : -kCos(r);
}

export function dcos(x: number): number {
  if (x !== x || x === Infinity || x === -Infinity) return NaN;
  if (x > -PIO4 && x < PIO4) return kCos(x);
  const n = reduce(x);
  const r = reducedR;
  return n === 0 ? kCos(r) : n === 1 ? -kSin(r) : n === 2 ? -kCos(r) : kSin(r);
}

/* ------------------------------------------------------------ atan/atan2 */

const ATAN_HI = [4.63647609000806093515e-1, 7.85398163397448278999e-1, 9.82793723247329054082e-1, 1.57079632679489655800];
const ATAN_LO = [2.26987774529616870924e-17, 3.06161699786838301793e-17, 1.39033110312309984516e-17, 6.12323399573676603587e-17];
const AT = [
  3.33333333333329318027e-1, -1.99999999998764832476e-1, 1.42857142725034663711e-1, -1.11111104054623557880e-1,
  9.09088713343650656196e-2, -7.69187620504482999495e-2, 6.66107313738753120669e-2, -5.83357013379057348645e-2,
  4.97687799461593236017e-2, -3.65315727442169155270e-2, 1.62858201153657823623e-2,
];

export function datan(x: number): number {
  if (x !== x) return NaN;
  const neg = x < 0;
  let a = neg ? -x : x;
  if (a >= 7.37869762948382e19) return neg ? -(ATAN_HI[3] + ATAN_LO[3]) : ATAN_HI[3] + ATAN_LO[3]; // |x| ≥ 2^66
  let id: number;
  if (a < 0.4375) {
    if (a < 7.450580596923828e-9) return x; // |x| < 2^-27
    id = -1;
  } else if (a < 1.1875) {
    if (a < 0.6875) {
      id = 0;
      a = (2 * a - 1) / (2 + a);
    } else {
      id = 1;
      a = (a - 1) / (a + 1);
    }
  } else if (a < 2.4375) {
    id = 2;
    a = (a - 1.5) / (1 + 1.5 * a);
  } else {
    id = 3;
    a = -1 / a;
  }
  const z = a * a;
  const w = z * z;
  const s1 = z * (AT[0] + w * (AT[2] + w * (AT[4] + w * (AT[6] + w * (AT[8] + w * AT[10])))));
  const s2 = w * (AT[1] + w * (AT[3] + w * (AT[5] + w * (AT[7] + w * AT[9]))));
  const r = id < 0 ? a - a * (s1 + s2) : ATAN_HI[id] - (a * (s1 + s2) - ATAN_LO[id] - a);
  return neg ? -r : r;
}

const PI = 3.1415926535897931160;
const PI_LO = 1.2246467991473531772e-16;
const PIO2 = 1.57079632679489655800;

export function datan2(y: number, x: number): number {
  if (x !== x || y !== y) return NaN;
  if (x === 1) return datan(y);
  if (y === 0) return x > 0 || Object.is(x, 0) ? y : Object.is(y, -0) ? -PI : PI;
  if (x === 0) return y > 0 ? PIO2 : -PIO2;
  if (x === Infinity || x === -Infinity || y === Infinity || y === -Infinity) {
    if (x === Infinity) return y === Infinity ? PIO2 / 2 : y === -Infinity ? -PIO2 / 2 : y > 0 ? 0 : -0;
    if (x === -Infinity) return y === Infinity ? (3 * PI) / 4 : y === -Infinity ? (-3 * PI) / 4 : y > 0 ? PI : -PI;
    return y > 0 ? PIO2 : -PIO2;
  }
  const z = datan(Math.abs(y / x));
  if (x > 0) return y < 0 ? -z : z;
  return y < 0 ? -(PI - (z - PI_LO)) : PI - (z - PI_LO);
}

/* ------------------------------------------------------------------ hypot */

/** sqrt(x² + y²) — exact ops only (game magnitudes never overflow). */
export function dhypot(x: number, y: number): number {
  return Math.sqrt(x * x + y * y);
}

/* -------------------------------------------------------------- exp/log/pow */

const LN2_HI = 6.93147180369123816490e-1;
const LN2_LO = 1.90821492927058770002e-10;
const INV_LN2 = 1.44269504088896338700;
const P1 = 1.66666666666666019037e-1;
const P2 = -2.77777777770155933842e-3;
const P3 = 6.61375632143793436117e-5;
const P4 = -1.65339022054652515390e-6;
const P5 = 4.13813679705723846039e-8;

export function dexp(x: number): number {
  if (x !== x) return NaN;
  if (x > 709.782712893383973096) return Infinity;
  if (x < -745.13321910194110842) return 0;
  if (x > -3.725290298461914e-9 && x < 3.725290298461914e-9) return 1 + x; // |x| < 2^-28
  const k = Math.round(x * INV_LN2);
  const hi = x - k * LN2_HI;
  const lo = k * LN2_LO;
  const r = hi - lo;
  const t = r * r;
  const c = r - t * (P1 + t * (P2 + t * (P3 + t * (P4 + t * P5))));
  const y = 1 - (lo - (r * c) / (2 - c) - hi);
  return k === 0 ? y : scalbn(y, k);
}

const LG1 = 6.666666666666735130e-1;
const LG2 = 3.999999999940941908e-1;
const LG3 = 2.857142874366239149e-1;
const LG4 = 2.222219843214978396e-1;
const LG5 = 1.818357216161805012e-1;
const LG6 = 1.531383769920937332e-1;
const LG7 = 1.479819860511658591e-1;
const SQRT2 = 1.4142135623730951;

export function dlog(x: number): number {
  if (x !== x || x < 0) return NaN;
  if (x === 0) return -Infinity;
  if (x === Infinity) return Infinity;
  // frexp via bits: x = m · 2^k with m ∈ [1, 2)
  let k = 0;
  if (x < 2.2250738585072014e-308) {
    x *= 18014398509481984; // 2^54: bring subnormals into the normal range
    k = -54;
  }
  f64[0] = x;
  const hi = u32[HI];
  k += ((hi >>> 20) & 0x7ff) - 1023;
  u32[HI] = (hi & 0x000fffff) | 0x3ff00000;
  let m = f64[0];
  if (m > SQRT2) {
    m *= 0.5;
    k++;
  }
  const f = m - 1;
  const s = f / (2 + f);
  const z = s * s;
  const w = z * z;
  const t1 = w * (LG2 + w * (LG4 + w * LG6));
  const t2 = z * (LG1 + w * (LG3 + w * (LG5 + w * LG7)));
  const R = t2 + t1;
  const hfsq = 0.5 * f * f;
  return k * LN2_HI - (hfsq - (s * (hfsq + R) + k * LN2_LO) - f);
}

/** a^b for a > 0 (the only case the sim needs); integers ≥ 0 exponents are exact. */
export function dpow(a: number, b: number): number {
  if (b === 0) return 1;
  if (Number.isInteger(b) && b > 0 && b <= 64) {
    let r = 1;
    let base = a;
    let e = b;
    while (e > 0) {
      if (e & 1) r *= base;
      base *= base;
      e >>= 1;
    }
    return r;
  }
  if (a === 0) return b > 0 ? 0 : Infinity;
  if (a < 0) return NaN;
  return dexp(b * dlog(a));
}
